import type { CmmdBoardTask, CmmdTaskStatus } from "@cmmd-forge/contracts";
import type { SidebarThreadSummary } from "~/types";
import { resolveTaskThreadFromIndex } from "~/lib/taskThreadLink";
import { getStatusConfig } from "~/components/command-center/statusConfig";
import { TASK_STATUS_OPTIONS, normalizeTaskStatus } from "./taskViewTypes";

export interface TaskStatusCount {
  readonly status: CmmdTaskStatus;
  readonly label: string;
  readonly count: number;
}

export interface TaskSummary {
  readonly total: number;
  readonly done: number;
  readonly progress: number; // 0..1
  readonly activeAgents: number;
  readonly byStatus: readonly TaskStatusCount[];
}

/**
 * Roll up a project's active (non-archived) tasks for the overview: counts by
 * status, completion progress, and how many tasks have a live running agent.
 */
export function summarizeTasks(
  tasks: readonly CmmdBoardTask[],
  threadIndex: ReadonlyMap<string, SidebarThreadSummary>,
): TaskSummary {
  const active = tasks.filter((t) => !t.archived);
  const counts = new Map<CmmdTaskStatus, number>();
  let activeAgents = 0;

  for (const task of active) {
    const status = normalizeTaskStatus(task.status);
    counts.set(status, (counts.get(status) ?? 0) + 1);
    if (resolveTaskThreadFromIndex(threadIndex, task.id)?.agentStatus === "running") {
      activeAgents += 1;
    }
  }

  const done = counts.get("done") ?? 0;
  const total = active.length;
  const byStatus = TASK_STATUS_OPTIONS.flatMap((status) => {
    const count = counts.get(status) ?? 0;
    return count > 0 ? [{ status, label: getStatusConfig(status).label, count }] : [];
  });

  return {
    total,
    done,
    progress: total === 0 ? 0 : done / total,
    activeAgents,
    byStatus,
  };
}
